import React from 'react';

import Dot from 'components/shared/Dot/Dot';
import {
  getGroupedMedicationData,
  getMedicationDoseText
} from 'components/shared/Forms/Custom/ProviderMedications/lib/medicationGroupHelpers';
import { isMedicationReadOnly } from 'components/shared/Forms/Custom/lib/aggregatedDataHelper';
import Icon from 'components/shared/Icon/Icon';
import Status from 'components/shared/Status/Status';

import Actions from '../components/Actions';

export const MEDICATION_STATUS = { ACTIVE: 'active', INACTIVE: 'inactive', DELETED: 'deleted' };

const disableRow = (params, userPermissions) => {
  const isReadOnly = isMedicationReadOnly({
    canUserUpdatePatientDocuments: userPermissions?.form?.update_patient_documents,
    data: params?.data
  });

  return isReadOnly ? { 'pointer-events': 'none', opacity: '0.4' } : {};
};

const MedicationCellContent = ({ params, props }) => {
  const medication = getGroupedMedicationData(params);

  if (!medication) {
    return null;
  }

  const isMedicationActive = medication?.status === MEDICATION_STATUS.ACTIVE;
  const isPatient = medication?.patient_id === medication?.created_by;

  return (
    <div className="flex h-full w-full items-center gap-2">
      <div className="no-row-select">
        <Icon
          icon="document-preview-mono"
          shade={600}
          color="neutral"
          onClick={() => props.setShowMedicationModal({ open: true, data: medication })}
        />
      </div>
      <Dot size="md" color={isMedicationActive ? 'success' : 'danger'} />

      <span className="truncate text-sm font-400 text-neutral-900">{medication?.medication}</span>

      {isPatient && <Icon icon="patient-response-mono" shade={600} />}
    </div>
  );
};

export default (props) => [
  {
    field: 'medication',
    headerName: 'Medication',
    cellRenderer: 'agGroupCellRenderer',
    showRowGroup: true,
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params) => <MedicationCellContent params={params} props={props} />
    },
    headerCheckboxSelection: props?.showCheckboxes,
    checkboxSelection: (params) =>
      !params?.node?.group &&
      props?.showCheckboxes &&
      !isMedicationReadOnly({
        canUserUpdatePatientDocuments: props?.userPermissions?.form?.update_patient_documents,
        data: getGroupedMedicationData(params)
      }),
    flex: 2
  },
  {
    field: 'status',
    headerName: 'Status',
    cellRenderer: (params) => {
      const medication = getGroupedMedicationData(params);

      if (!medication?.status) return null;

      const status = medication?.status;
      return <Status status={status} />;
    }
  },
  {
    field: 'dose',
    headerName: 'Dose',
    cellRenderer: (params) => {
      const medication = getGroupedMedicationData(params);

      if (!medication) {
        return null;
      }
      const content = getMedicationDoseText(medication);
      return content ? <span>{content}</span> : '-';
    }
  },
  {
    field: 'product_ndc',
    headerName: 'NDC Code',
    valueGetter: (params) => getGroupedMedicationData(params)?.product_ndc,
    valueFormatter: ({ value }) => (!!value ? value : '-')
  },
  {
    field: 'prescriber.label',
    headerName: 'Prescriber',
    valueGetter: (params) => getGroupedMedicationData(params)?.prescriber?.label,
    valueFormatter: ({ value }) => (!!value ? value : '-')
  },
  {
    field: 'start_date',
    headerName: 'Start date',
    valueGetter: (params) => getGroupedMedicationData(params)?.start_date,
    valueFormatter: ({ value }) => (value ? value : '-')
  },
  {
    field: 'end_date',
    headerName: 'End date',
    valueGetter: (params) => getGroupedMedicationData(params)?.end_date,
    valueFormatter: ({ value }) => (value ? value : '-')
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: (params) => {
      if (params?.node?.group || !params?.data) {
        return null;
      }

      return <Actions data={params?.data} customProps={props} />;
    },
    maxWidth: 100,
    pinned: 'right',
    cellStyle: (params) => disableRow(params, props.userPermissions)
  }
];
